import { useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { useLanguage } from "@/lib/i18n";
import { blogArticles as articles } from "@/lib/blog/articles";
import { ArticleCard } from "@/components/blog-list/ArticleCard";
import {
  applyPageSeo,
  breadcrumbLd,
  upsertJsonLd,
  SITE_URL,
} from "@/lib/seo";

const content = {
  fr: {
    eyebrow: "Ressources",
    heading: "Nos articles",
    description:
      "Guides pratiques, comparatifs et conseils pour mieux gérer vos reçus et votre budget au quotidien.",
  },
  en: {
    eyebrow: "Resources",
    heading: "Our articles",
    description:
      "Practical guides, comparisons and tips to better manage your receipts and daily budget.",
  },
};

const BlogList = () => {
  const { language } = useLanguage();
  const c = content[language];

  useEffect(() => {
    const path = language === "fr" ? "/blog" : "/en/blog";
    const url = `${SITE_URL}${path}`;
    applyPageSeo({
      lang: language,
      title:
        language === "fr"
          ? "Blog ReFound — Guides sur les reçus, retours et garanties"
          : "ReFound Blog — Guides on receipts, returns and warranties",
      description: c.description,
      path,
      frPath: "/blog",
      enPath: "/en/blog",
    });
    upsertJsonLd("itemlist", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${url}#blog`,
      url,
      name: language === "fr" ? "Blog ReFound" : "ReFound Blog",
      inLanguage: language === "fr" ? "fr-CA" : "en-CA",
      hasPart: articles.map((a) => ({
        "@type": "BlogPosting",
        headline: a[language].title,
        url: `${SITE_URL}${language === "fr" ? a.frPath : a.enPath}`,
      })),
    });
    upsertJsonLd(
      "breadcrumb",
      breadcrumbLd([
        { name: language === "fr" ? "Accueil" : "Home", url: language === "fr" ? `${SITE_URL}/` : `${SITE_URL}/en` },
        { name: "Blog", url },
      ]),
    );
  }, [language, c.description]);

  return (
    <Layout>
      {/* Hero header */}
      <section className="relative pt-28 pb-12 md:pt-36 md:pb-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-primary mb-4">
            {c.eyebrow}
          </span>
          <h1 className="font-['Funnel_Display'] text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-foreground mb-5">
            {c.heading}
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
            {c.description}
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <hr className="border-border/30" />
      </div>

      {/* Article listing */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="flex flex-col gap-6">
            {articles.map((article) => (
              <ArticleCard
                key={article.slug}
                article={article}
                language={language}
                href={language === "fr" ? article.frPath : article.enPath}
              />
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogList;
